import MenuIcon from '@mui/icons-material/Menu';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import type { Theme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { ReactNode, Ref } from 'react';

import { AccountMenu } from './AccountMenu';
import { GlobalSearch } from './GlobalSearch';
import { NotificationsPanel } from './NotificationsPanel';
import { OrganizationSwitcher } from './OrganizationSwitcher';

interface TopBarProps {
  /** Opens the navigation drawer on narrow screens. */
  onOpenNav: () => void;
  /** Folds or unfolds the left navigation on a desktop. */
  onToggleFold?: () => void;
  /** Whether the left navigation is folded to icons. */
  navFolded?: boolean;
  /** A row above the bar - the staff viewing banner, while it is on. */
  banner?: ReactNode;
  /** The whole bar, banner included, so the layout can measure its height. */
  ref?: Ref<HTMLElement>;
}

export function TopBar({
  onOpenNav,
  onToggleFold,
  navFolded = false,
  banner,
  ref,
}: TopBarProps) {
  /*
   * One control, two jobs, as the wireframe draws it beside the logo. On a
   * narrow screen the navigation is a drawer, and this opens it. On a desktop
   * the navigation is always there, and this folds it to icons and back.
   */
  const desktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));

  return (
    <AppBar
      ref={ref}
      position="fixed"
      color="inherit"
      elevation={0}
      sx={{
        zIndex: (t) => t.zIndex.drawer + 1,
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      {/* Above the bar, not below it: everything under the banner is then
          the organization's portal exactly as its own people see it. */}
      {banner}
      <Toolbar sx={{ gap: 2 }}>
        {desktop ? (
          <IconButton
            onClick={onToggleFold}
            aria-label={navFolded ? 'Expand navigation' : 'Collapse navigation'}
            // Says whether the full navigation is showing, so a screen reader
            // hears the state as well as the action.
            aria-expanded={!navFolded}
            edge="start"
          >
            <MenuIcon />
          </IconButton>
        ) : (
          <IconButton onClick={onOpenNav} aria-label="Open navigation" edge="start">
            <MenuIcon />
          </IconButton>
        )}

        <Typography variant="h6" component="span" sx={{ fontWeight: 600 }} noWrap>
          ActivistARC
        </Typography>

        {/* A plain label for one organization, a menu for several. It reads
            the signed-in person itself rather than taking a name as a prop,
            because switching changes their role too. */}
        <OrganizationSwitcher />

        <Box sx={{ flexGrow: 1 }} />

        <GlobalSearch />

        <NotificationsPanel />

        <AccountMenu />
      </Toolbar>
    </AppBar>
  );
}
